import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { AuthService } from "@front/m19-services";
import {
  IAlertComponent,
  IButtonDirective,
  ResetPasswordFormComponent,
  ResetPasswordFormValue,
  SpinnerComponent,
  ToasterService,
} from "@front/m19-ui";
import { switchMap, tap } from "rxjs/operators";
import { throwError } from "rxjs";
import { User } from "@front/m19-api-client";
import { TranslocoDirective } from "@jsverse/transloco";

@UntilDestroy()
@Component({
  selector: "app-password-reset",
  templateUrl: "password-reset.component.html",
  imports: [
    TranslocoDirective,
    IAlertComponent,
    ResetPasswordFormComponent,
    IButtonDirective,
    RouterLink,
    SpinnerComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PasswordResetComponent implements OnInit {
  private readonly authService = inject(AuthService);
  private readonly toasterService = inject(ToasterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  loading = signal(false);
  success = signal(false);
  error = signal<string | null>(null);
  user = signal<User | null | undefined>(undefined);

  ngOnInit(): void {
    const token = this.route.snapshot.queryParamMap.get("token");
    if (!token) {
      this.user.set(null);
      return;
    }
    this.authService.setAccessToken(token, 3600, false);
    this.authService
      .isLoggedIn()
      .pipe(
        switchMap((loggedIn) => {
          if (!loggedIn) {
            return throwError(() => {});
          }
          return this.authService.loggedUser$;
        }),
        untilDestroyed(this),
      )
      .subscribe({
        next: (user) => {
          this.user.set({ ...user });
        },
        error: () => {
          this.user.set(null);
        },
      });
  }

  requestPasswordReset(formValue: ResetPasswordFormValue): void {
    const user = this.user();
    if (!formValue || !user) return;
    this.loading.set(true);
    user.newPassword = formValue.password;
    this.user.set(user);

    this.authService
      .updatePassword(user)
      .pipe(
        tap({
          next: () => this.toasterService.success("Password successfully updated"),
          error: (error) => {
            this.error.set("Error updating password: " + error);
            this.loading.set(false);
          },
        }),
        switchMap(() => this.authService.reloadUser()),
      )
      .subscribe({
        next: () => {
          this.router.navigate(["dashboard"]);
          this.loading.set(false);
        },
        error: (error: string) => {
          this.error.set("Error: " + error);
          this.loading.set(false);
        },
      });
  }
}
